<script lang="ts">
  import { api, OfflineError } from '../lib/api'
  import { config } from '../lib/config'
  import { cardExpired, cardExpiresBefore, formatCardExpiry, formatDate, formatMoney } from '../lib/format'
  import type { Subscription } from '../lib/types'
  import { ripple } from '../lib/ripple'
  import Notice from '../lib/components/Notice.svelte'
  import Loading from '../lib/components/Loading.svelte'
  import ConfirmDialog from '../lib/components/ConfirmDialog.svelte'

  /*
   * Billing info — the Website's /account/billing, state for state.
   *
   * The branching below is not incidental complexity: each arm is a different thing that has gone right
   * or wrong with a renewal, and the Website already distinguishes them. Collapsing them into "your
   * subscription has a problem" would be a regression dressed as a simplification.
   *
   * STRIPE IS NOT LOADED HERE. The Website pulls in js.stripe.com/v3 and calls redirectToCheckout with a
   * session id; the billing API returns the Checkout session's own URL instead and this navigates to it.
   * No third-party script, no publishable key in the page, and nothing to add to script-src.
   */

  let subscription = $state<Subscription | null>(null)
  let failed = $state(false)
  let error = $state<string | null>(null)
  let leaving = $state(false)
  let removing = $state(false)
  let confirmRemove = $state(false)

  async function load() {
    try {
      subscription = await api.subscription()
    } catch {
      failed = true
    }
  }

  load()

  async function toCheckout() {
    if (leaving) return
    leaving = true
    error = null
    try {
      const { url } = await api.checkoutSession('/billing')
      location.href = url
    } catch (e) {
      // Only re-enable on failure: on success the page is navigating away, and re-enabling would flash
      // an active button on a page that is leaving.
      leaving = false
      error =
        e instanceof OfflineError
          ? 'Could not reach Bird Journal. Check your connection and try again.'
          : 'We could not start the payment process just now. Please try again.'
    }
  }

  async function removeCard() {
    removing = true
    error = null
    try {
      await api.removePaymentMethod()
      confirmRemove = false
      subscription = null
      await load()
    } catch (e) {
      confirmRemove = false
      error =
        e instanceof OfflineError
          ? 'Could not reach Bird Journal. Check your connection and try again.'
          : 'We could not remove your card just now. Please try again.'
    } finally {
      removing = false
    }
  }

  /*
   * Which renewal story this customer is in. Derived once so the markup reads as a list of cases rather
   * than a nest of conditions, and so the "may expire" footnote has one thing to test.
   */
  type Situation =
    | 'not-premium'
    | 'renews-on-credit'
    | 'renews-on-card'
    | 'past-due'
    | 'card-expired'
    | 'card-expires-first'
    | 'ending'

  const situation = $derived.by((): Situation => {
    const s = subscription
    // A trial has no billing relationship, so it sees the upgrade path rather than a renewal story —
    // which is what the Website does, via `Model.Premium && !Model.Trial`.
    if (!s || s.state !== 'premium' || s.trial) return 'not-premium'
    if (!s.autoRenew) return 'ending'
    if (s.pastDue) return 'past-due'
    if (s.nextPayment === null) return 'renews-on-credit'
    if (s.card && cardExpired(s.card)) return 'card-expired'
    if (s.card && cardExpiresBefore(s.card, s.premiumUntil)) return 'card-expires-first'
    return 'renews-on-card'
  })

  const mayExpire = $derived(
    situation === 'past-due' ||
      situation === 'card-expired' ||
      situation === 'card-expires-first' ||
      situation === 'ending',
  )

  /** The last day payment is retried before a past-due subscription ends. */
  const retryUntil = $derived.by(() => {
    const until = subscription?.premiumUntil
    if (!until) return ''
    const date = new Date(until)
    date.setUTCDate(date.getUTCDate() + 7)
    return formatDate(date.toISOString())
  })

  const renewsOn = $derived(formatDate(subscription?.premiumUntil))
</script>

{#if failed}
  <Notice tone="error">We could not load your billing information just now. Please try again shortly.</Notice>
{:else if !subscription}
  <Loading rows={4} />
{:else}
  {#if error}
    <Notice tone="error">{error}</Notice>
  {/if}

  {#if situation === 'not-premium'}
    <Notice tone="info">
      You are currently using Bird Journal Free.
      {#if subscription.trial}
        Your premium trial runs until {renewsOn}.
      {/if}
    </Notice>
    <p>
      Bird Journal Premium adds graphs, maps and reports.
      <a href="{config().marketing}/premium" data-external>Learn more about Bird Journal Premium</a>.
    </p>
  {:else}
    {#if situation === 'past-due'}
      <Notice tone="error">
        <p>
          Your premium subscription was due to renew for {formatMoney(subscription.nextPayment!)} on {renewsOn},
          but payment using your registered card failed.
        </p>
        <p>
          Update your card details below to make sure your subscription continues uninterrupted. Payment
          will be attempted until {retryUntil}, at which point your premium subscription will end.
        </p>
      </Notice>
    {:else if situation === 'renews-on-credit'}
      <!-- Not `live`: these describe the state of the subscription on arrival, not the outcome of
           something the customer just did. See Notice.svelte. -->
      <Notice tone="ok">
        Your premium subscription will automatically renew on {renewsOn} using existing credit on your
        account. No payment will be taken from your registered card.
      </Notice>
    {:else if situation === 'card-expired'}
      <Notice tone="error">
        <p>
          Your premium subscription is due to renew for {formatMoney(subscription.nextPayment!)} on {renewsOn},
          but your registered card has expired.
        </p>
        <p>Update your card details below to make sure it continues uninterrupted. No payment will be taken now.</p>
      </Notice>
    {:else if situation === 'card-expires-first'}
      <Notice tone="error">
        <p>
          Your premium subscription is due to renew for {formatMoney(subscription.nextPayment!)} on {renewsOn},
          but your registered card will have expired by then.
        </p>
        <p>Update your card details below to make sure it continues uninterrupted. No payment will be taken now.</p>
      </Notice>
    {:else if situation === 'renews-on-card'}
      <Notice tone="ok">
        Your premium subscription will automatically renew for {formatMoney(subscription.nextPayment!)} on {renewsOn},
        using your registered card.
      </Notice>
    {:else if situation === 'ending'}
      <Notice tone="warn">
        <p>Your premium subscription is due to end on {renewsOn}.</p>
        {#if subscription.nextPayment}
          <p>
            Add card details below to enable automatic renewal and make sure your subscription continues
            uninterrupted. Your next payment will be for {formatMoney(subscription.nextPayment)} on {renewsOn};
            no payment will be taken now.
          </p>
        {:else}
          <p>
            Add card details below to enable automatic renewal and make sure your subscription continues
            uninterrupted. Your next payment will use existing credit on your account and no payment will
            be taken now — your card will be used for future renewals.
          </p>
        {/if}
      </Notice>
    {/if}

    {#if subscription.card}
      <div class="card-summary">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 14H4v-6h16Zm0-10H4V6h16Z" />
        </svg>
        <div>
          <div class="card-line">{subscription.card.brand} ending in {subscription.card.last4}</div>
          <div class="card-expiry">Expires {formatCardExpiry(subscription.card)}</div>
        </div>
      </div>
      {#if subscription.autoRenew}
        <p class="aside">To turn off automatic renewal, remove your card.</p>
      {/if}
    {/if}

    <!-- The purchase restriction REPLACES the buttons rather than sitting beside them, which is what the
         Website does: if we cannot sell to somebody, offering them a payment form is worse than useless. -->
    {#if subscription.saleRestriction?.code === 'EuVat'}
      <Notice tone="info">
        <p>
          Regrettably, due to changes in European tax law, we are unable to sell directly to customers in
          the European Union at this time.
        </p>
        <p class="last">
          You can renew by purchasing a one-year subscription from one of our mobile apps, via the Apple
          App Store or Google Play, once your current period has ended.
        </p>
      </Notice>
    {:else if subscription.saleRestriction?.code === 'JapanTax'}
      <Notice tone="info">
        <p>
          Regrettably, due to changes in Japanese tax law, we are unable to sell to customers in Japan at
          this time. You may continue to use a free Bird Journal account.
        </p>
        <p class="last">
          Please <a href="https://support.birdjournal.com/" data-external>get in touch via our support site</a>
          if we can help.
        </p>
      </Notice>
    {:else}
      <div class="btn-row actions">
        <button class="btn btn-accent rip" use:ripple type="button" onclick={toCheckout} disabled={leaving}>
          {leaving ? 'Opening…' : subscription.card ? 'Update card' : 'Add card'}
        </button>
        {#if subscription.card && subscription.autoRenew}
          <button
            class="btn btn-outlined rip"
            use:ripple
            type="button"
            onclick={() => (confirmRemove = true)}
          >
            Remove card
          </button>
        {/if}
      </div>
    {/if}

    {#if mayExpire}
      <p class="aside">
        When a premium subscription ends your account reverts to a free account. All your data can still
        be accessed with the free account, and you can upgrade again at any time.
      </p>
    {/if}
  {/if}
{/if}

<ConfirmDialog
  bind:open={confirmRemove}
  title="Remove card"
  confirmLabel={removing ? 'Removing…' : 'Remove card'}
  destructive
  busy={removing}
  onconfirm={removeCard}
>
  <p>Are you sure you want to remove this card?</p>
  <p>
    Removing it disables automatic renewal, so your premium subscription will end on {renewsOn}.
  </p>
</ConfirmDialog>

<style>
  .card-summary {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5);
    background: var(--bj-surface);
    border: 1px solid var(--bj-divider);
    border-radius: var(--corner-lg);
    margin-bottom: var(--space-4);
  }
  .card-summary svg {
    color: var(--bj-on-surface-variant);
    flex: none;
  }
  .card-line {
    font-weight: 500;
  }
  .card-expiry {
    color: var(--bj-on-surface-variant);
    font-size: 0.9rem;
  }
  .actions {
    margin: var(--space-6) 0;
  }
  .aside {
    color: var(--bj-on-surface-variant);
    font-size: 0.9rem;
  }
  .last {
    margin-bottom: 0;
  }
</style>
